import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useSubscriptionContext,
  callFunction,
  useAuth,
} from '@platform/core';

interface OrgUser {
  id: string;
  displayName?: string;
  email?: string;
  role?: string;
  status?: string;
  isPending?: boolean;
  lastLoginAt?: string;
  createdAt?: unknown;
}

export function TeamPage() {
  const { subscription, planLimits } = useSubscriptionContext();
  const { userProfile } = useAuth();
  const organizationId = subscription?.organizationId || userProfile?.organizationId || '';

  const [members, setMembers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org_user');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Remove dialog state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<OrgUser | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await callFunction<
        { organizationId: string },
        { success: boolean; users: OrgUser[] }
      >('getOrgUsers', { organizationId });

      setMembers(result.users || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load team members';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const activeUsersCount = members.filter((m) => !m.isPending).length;
  const atLimit = activeUsersCount >= planLimits.maxUsers;
  const usersPercent = Math.min(100, (activeUsersCount / planLimits.maxUsers) * 100);

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !organizationId) return;
    setInviteLoading(true);
    setError(null);

    try {
      await callFunction('addUserToOrganization', {
        userId: inviteEmail.trim(), // The backend expects userId; for invite-by-email, use createInvite
        organizationId,
        role: inviteRole,
      });
      setSuccess('User added successfully');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('org_user');
      fetchMembers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to invite user';
      setError(message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveClick = (user: OrgUser) => {
    setUserToRemove(user);
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove || !organizationId) return;
    setRemoveLoading(true);
    setError(null);

    try {
      await callFunction('removeUserFromOrganization', {
        organizationId,
        userId: userToRemove.id,
      });
      setSuccess(`${userToRemove.displayName || userToRemove.email || 'User'} removed from organization`);
      setRemoveDialogOpen(false);
      setUserToRemove(null);
      fetchMembers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove user';
      setError(message);
    } finally {
      setRemoveLoading(false);
    }
  };

  const getRoleChip = (role?: string) => {
    switch (role) {
      case 'admin':
        return <Chip label="Admin" color="error" size="small" />;
      case 'org_admin':
        return <Chip label="Org Admin" color="warning" size="small" />;
      case 'consultant':
        return <Chip label="Consultant" color="info" size="small" />;
      case 'firm_lead':
        return <Chip label="Firm Lead" color="secondary" size="small" />;
      case 'viewer':
        return <Chip label="Viewer" color="default" size="small" />;
      default:
        return <Chip label={role || 'User'} size="small" />;
    }
  };

  const getStatusChip = (user: OrgUser) => {
    if (user.isPending) {
      return <Chip label="Pending" color="warning" size="small" variant="outlined" />;
    }
    return <Chip label="Active" color="success" size="small" variant="outlined" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Team Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Usage bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1">
            Team Members: {activeUsersCount} of {planLimits.maxUsers} users
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteOpen(true)}
            disabled={atLimit}
          >
            {atLimit ? 'Plan Limit Reached' : 'Add Member'}
          </Button>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usersPercent}
          color={usersPercent > 80 ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
        {atLimit && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            You've reached your plan limit. Upgrade to add more users.
          </Typography>
        )}
      </Paper>

      {/* Members table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No team members found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.displayName || '—'}</TableCell>
                  <TableCell>{user.email || '—'}</TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>{getStatusChip(user)}</TableCell>
                  <TableCell>
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remove from organization">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveClick(user)}
                        disabled={user.id === userProfile?.uid}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the user ID to add to your organization.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="User ID"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Select
            fullWidth
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            label="Role"
          >
            <MenuItem value="org_user">User</MenuItem>
            <MenuItem value="org_admin">Org Admin</MenuItem>
            <MenuItem value="viewer">Viewer</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim()}
          >
            {inviteLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>{userToRemove?.displayName || userToRemove?.email || 'this user'}</strong>{' '}
            from the organization? They will lose access to all organization resources.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRemoveConfirm}
            color="error"
            variant="contained"
            disabled={removeLoading}
          >
            {removeLoading ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TeamPage;
